import { useMemo } from "react";
import type { Product } from "../../../../redux/api";

// Ported from mobileapp's stockLevel.ts + ProductStats.tsx. Two places have to
// agree on this rule — the figure on a row and the counts above the list — so
// it lives in one file. Without a reorderLevel there is no threshold to be
// under, so anything in stock counts as `ok`.
export type StockLevel = "out" | "low" | "ok";

export const stockLevel = (product: Product): StockLevel => {
  if (product.totalStock <= 0) return "out";
  if (
    typeof product.reorderLevel === "number" &&
    product.totalStock <= product.reorderLevel
  ) {
    return "low";
  }
  return "ok";
};

export interface ProductTotals {
  total: number;
  value: number;
  lowStock: number;
  outOfStock: number;
  currency?: string;
}

/**
 * Figures for the strip above the product list, derived from the products
 * already in hand — there is no stats endpoint. They describe the WHOLE
 * catalogue, not the filtered view.
 */
export const useProductTotals = (products: Product[]): ProductTotals =>
  useMemo(() => {
    let value = 0;
    let lowStock = 0;
    let outOfStock = 0;

    for (const product of products) {
      // Cost basis, which is what "inventory value" means in stock accounting:
      // what the goods cost, not what they might sell for.
      value += (product.purchasePrice ?? 0) * (product.totalStock ?? 0);

      const level = stockLevel(product);
      if (level === "low") lowStock += 1;
      if (level === "out") outOfStock += 1;
    }

    return {
      total: products.length,
      value,
      lowStock,
      outOfStock,
      currency: products[0]?.currency,
    };
  }, [products]);
